<script setup lang="ts">
/**
 * Knowledge page for one entity: header, AI summary, body, facts with
 * provenance, related entities, "mentioned in", a 1-hop mini-map, history
 * (diff + restore) and editing.
 */
import { Archive, BookOpen, ChevronDown, ChevronRight, Clock, Copy, EllipsisVertical, FileText, History, Maximize2, Network, PencilLine, SearchX, UserRound, Zap } from "@lucide/vue";
import { useClipboard } from "@vueuse/core";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, reactive, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { toast } from "vue-sonner";
import { isProblem } from "@/api/client";
import type { Entity, GraphNode, RelatedEntity } from "@/api/contracts";
import { archiveEntity, useEntity, useGraph } from "@/api/knowledge";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import MarkdownView from "@/components/common/MarkdownView.vue";
import PageTabs from "@/components/common/PageTabs.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import SectionCard from "@/components/common/SectionCard.vue";
import GraphCanvas from "@/components/graph/GraphCanvas.vue";
import AiSummaryBox from "@/components/knowledge/AiSummaryBox.vue";
import EntityEditor from "@/components/knowledge/EntityEditor.vue";
import EntityHistory from "@/components/knowledge/EntityHistory.vue";
import FactsCard from "@/components/knowledge/FactsCard.vue";
import TypeBadge from "@/components/knowledge/TypeBadge.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { confirm } from "@/composables/useConfirm";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";
import { errorMessage } from "@/lib/errors";
import { entityPath, freshness, typeInfo } from "@/lib/knowledge";
import { extractHeadings } from "@/lib/markdown";
import { cn } from "@/lib/utils";

definePage({ meta: { title: "Knowledge", requires: { capability: "knowledge.read" } } });

const route = useRoute("/k/[type]/[slug]");
const router = useRouter();
const queryClient = useQueryClient();
const { can } = useSession();
const { copy } = useClipboard({ legacy: true });

const type = computed(() => route.params.type);
const slug = computed(() => route.params.slug);
const page = useEntity(type, slug);
const data = computed(() => page.data.value);
const entity = computed(() => data.value?.entity);
/** A runbook page can point at an action (prop `action`, or the tag `action-<name>` that the action page lists). */
const linkedAction = computed(() => {
  const e = entity.value;
  if (!e || e.type !== "runbook") return null;
  const prop = e.props?.action;
  if (typeof prop === "string" && /^[a-z0-9][a-z0-9-]{1,62}$/.test(prop)) return prop;
  return e.tags.find((x) => x.startsWith("action-"))?.slice("action-".length) || null;
});

// The service resolves names, aliases, old slugs and merged pages: move to the canonical address.
watch(entity, (e) => {
  if (e && (e.type !== type.value || e.slug !== slug.value)) void router.replace({ path: entityPath(e.type, e.slug), query: route.query });
});

const tab = computed(() => (route.query.tab === "history" ? "history" : "overview"));
const tabModel = computed({ get: () => tab.value, set: (id: string | undefined) => setQuery({ tab: id === "history" ? id : undefined }) });
const TABS = [
  { id: "overview", label: t.knowledge.overview, icon: BookOpen },
  { id: "history", label: t.knowledge.history, icon: History },
];
const editing = computed(() => route.query.edit === "1" && can("knowledge.write"));
function setQuery(q: Record<string, string | undefined>) {
  void router.replace({ query: { ...route.query, ...q } });
}

const graph = useGraph("neighborhood", () => ({ uri: entity.value?.uri, depth: 1 }), () => Boolean(entity.value) && tab.value === "overview" && !editing.value);
const graphUseful = computed(() => (graph.data.value?.nodes.length ?? 0) > 1);

// Owner and freshness (stale = as in the server's health report: curated types, 180 days)
const CURATED_TYPES = ["solution", "runbook", "query", "concept", "decision", "topic"];
const OWNED_TYPES = ["system", "repo", "resource", "site"];
const STALE_DAYS = 180;
const owners = computed(() => (data.value?.related ?? []).filter((r) => r.predicate === "owned_by" && r.direction === "out").map((r) => r.entity));
const fresh = computed(() => freshness(entity.value?.updatedAt));
const ageDays = computed(() => (entity.value ? Math.floor((Date.now() - new Date(entity.value.updatedAt).getTime()) / 86_400_000) : 0));
const staleWarning = computed(() => Boolean(entity.value && CURATED_TYPES.includes(entity.value.type) && ageDays.value >= STALE_DAYS));
const FRESH_TONE = { fresh: "bg-success", aging: "bg-warning", stale: "bg-destructive", unknown: "bg-muted-foreground" } as const;
const FRESH_LABEL = { fresh: "Updated in the last 30 days", aging: "Updated 1–6 months ago", stale: "Not updated for 6 months or more", unknown: "Unknown" } as const;
const primaryLineage = computed(() => (entity.value?.type === "lineage" ? undefined : entity.value?.lineages[0]));

/** Related groups longer than this start collapsed. */
const RELATED_PREVIEW = 5;
const expanded = reactive(new Set<string>());
const toggleGroup = (label: string) => (expanded.has(label) ? expanded.delete(label) : expanded.add(label));

const relatedGroups = computed(() => {
  const map = new Map<string, RelatedEntity[]>();
  for (const r of data.value?.related ?? []) {
    const key = r.label;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(r);
  }
  return [...map.entries()];
});
const headings = computed(() => extractHeadings(entity.value?.bodyMd));
const hasAside = computed(() => Boolean(data.value?.facts.length || relatedGroups.value.length || graphUseful.value || headings.value.length > 2));
const notFound = computed(() => isProblem(page.error.value) && page.error.value.status === 404 && !page.error.value.notAvailable);

function onSaved(saved: Entity) {
  queryClient.setQueryData(["knowledge", "entity", saved.type, saved.slug], (old: unknown) => (old ? { ...(old as object), entity: saved } : old));
  void queryClient.invalidateQueries({ queryKey: ["knowledge"] });
  if (saved.slug !== slug.value || saved.type !== type.value) void router.replace(entityPath(saved.type, saved.slug));
  else setQuery({ edit: undefined });
}

async function archive() {
  if (!entity.value) return;
  const answer = await confirm({
    title: `Archive ${entity.value.name}?`,
    description: "The page disappears from search but stays in the history and can be restored.",
    confirmLabel: "Archive",
    destructive: true,
    reason: true,
  });
  if (!answer) return;
  try {
    await archiveEntity(entity.value.type, entity.value.slug, answer.reason);
    toast.success("Archived.");
    await queryClient.invalidateQueries({ queryKey: ["knowledge"] });
    void router.push("/knowledge");
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

function openNode(n: GraphNode) {
  if (n.type && n.slug) void router.push(entityPath(n.type, n.slug));
}

function copyLink() {
  void copy(window.location.origin + route.path);
  toast.success("Link copied.");
}
</script>

<template>
  <div>
    <ListSkeleton v-if="page.isPending.value" :rows="6" />
    <EmptyState
      v-else-if="notFound"
      :icon="SearchX"
      :title="`No ${typeInfo(type).label.toLowerCase()} page “${slug}”`"
      description="It may have been renamed or archived, or nobody has written it yet."
    >
      <Button as-child><RouterLink :to="{ path: '/knowledge/search', query: { q: slug.replace(/-/g, ' ') } }">Search for it</RouterLink></Button>
    </EmptyState>
    <ErrorState v-else-if="page.error.value" :error="page.error.value" @retry="page.refetch()" />

    <template v-else-if="data && entity">
      <!-- Header -->
      <header class="mb-6 flex flex-col gap-3">
        <nav aria-label="Breadcrumb">
          <ol class="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
            <li><RouterLink to="/knowledge" class="hover:text-foreground">{{ t.nav.knowledge }}</RouterLink></li>
            <template v-if="primaryLineage">
              <li aria-hidden="true"><ChevronRight class="size-3.5" /></li>
              <li><RouterLink :to="`/knowledge/lineage/${encodeURIComponent(primaryLineage)}`" class="hover:text-foreground">{{ primaryLineage }}</RouterLink></li>
            </template>
            <li aria-hidden="true"><ChevronRight class="size-3.5" /></li>
            <li>
              <RouterLink
                :to="{ path: '/knowledge/search', query: { types: entity.type, ...(primaryLineage ? { lineage: primaryLineage } : {}) } }"
                class="hover:text-foreground"
              >{{ typeInfo(entity.type).plural || entity.type }}</RouterLink>
            </li>
            <li aria-hidden="true"><ChevronRight class="size-3.5" /></li>
            <li aria-current="page" class="max-w-[16rem] truncate">{{ entity.name }}</li>
          </ol>
        </nav>
        <div class="flex flex-col gap-3 sm:flex-row sm:items-start">
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2">
              <h1 class="page-title">{{ entity.name }}</h1>
              <TypeBadge :type="entity.type" :subtype="entity.subtype" />
              <Badge v-if="entity.status && entity.status !== 'active'" variant="warning">{{ entity.status }}</Badge>
            </div>
            <p v-if="entity.aliases.length" class="mt-1 text-sm text-muted-foreground">Also known as {{ entity.aliases.join(" · ") }}</p>
            <div class="mt-2 flex flex-wrap items-center gap-1.5">
              <RouterLink
                v-for="l in entity.lineages"
                :key="l"
                :to="`/knowledge/lineage/${encodeURIComponent(l)}`"
                class="rounded-md bg-accent px-2 py-0.5 text-xs font-medium text-accent-foreground hover:bg-primary/15"
              >{{ l }}</RouterLink>
              <RouterLink v-for="tag in entity.tags" :key="tag" :to="{ path: '/knowledge/search', query: { tags: tag } }" class="text-xs text-muted-foreground hover:text-foreground">#{{ tag }}</RouterLink>
            </div>
            <p class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span v-if="owners.length" class="inline-flex items-center gap-1">
                <UserRound class="size-3.5" aria-hidden="true" /> {{ t.knowledge.owner }}:
                <template v-for="(o, i) in owners" :key="o.uri">
                  <RouterLink :to="entityPath(o.type, o.slug)" class="font-medium text-foreground hover:text-primary hover:underline">{{ o.name }}</RouterLink><span v-if="i < owners.length - 1">,</span>
                </template>
              </span>
              <span v-else-if="OWNED_TYPES.includes(entity.type)" class="inline-flex items-center gap-1">
                <UserRound class="size-3.5" aria-hidden="true" /> {{ t.knowledge.noOwner }}
              </span>
              <span class="inline-flex items-center gap-1.5" :title="FRESH_LABEL[fresh]">
                <span :class="['size-1.5 rounded-full', FRESH_TONE[fresh]]" aria-hidden="true" />
                <span>Edited <RelativeTime :value="entity.updatedAt" /> by {{ entity.updatedBy ?? "unknown" }} · revision {{ entity.revision }}</span>
              </span>
            </p>
          </div>
          <div v-if="!editing" class="flex shrink-0 gap-2">
            <Button v-if="linkedAction" as-child variant="outline"><RouterLink :to="`/actions/${linkedAction}`"><Zap /> {{ t.actions.linkFromKnowledge }}</RouterLink></Button>
            <Button v-if="can('knowledge.write')" @click="setQuery({ edit: '1', tab: undefined })"><PencilLine /> {{ t.common.edit }}</Button>
            <DropdownMenu>
              <DropdownMenuTrigger as-child>
                <Button variant="outline" size="icon" aria-label="More actions"><EllipsisVertical /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem @select="copyLink"><Copy /> Copy link</DropdownMenuItem>
                <DropdownMenuItem @select="copy(entity.uri)"><Copy /> Copy URI for Claude</DropdownMenuItem>
                <DropdownMenuItem as-child>
                  <RouterLink :to="{ path: '/knowledge/views', query: { view: 'neighborhood', uri: entity.uri } }"><Network /> Explore neighbourhood</RouterLink>
                </DropdownMenuItem>
                <DropdownMenuItem as-child>
                  <RouterLink :to="{ path: '/knowledge/views', query: { view: 'impact', uri: entity.uri } }"><Network /> What breaks if it's down?</RouterLink>
                </DropdownMenuItem>
                <template v-if="can('knowledge.write')">
                  <DropdownMenuSeparator />
                  <DropdownMenuItem class="text-destructive" @select="archive"><Archive /> Archive…</DropdownMenuItem>
                </template>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <!-- Edit mode -->
      <!-- Keyed by id only: a background refresh must never reset a draft (conflicts are handled on save) -->
      <EntityEditor v-if="editing" :key="entity.id" :page="data" @saved="onSaved" @cancel="setQuery({ edit: undefined })" />

      <template v-else>
        <PageTabs v-model="tabModel" :items="TABS" label="Page sections" id-prefix="entity-tab" class="mb-6" />

        <EntityHistory v-if="tab === 'history'" :entity="entity" @restored="page.refetch()" />

        <div v-else :class="cn('grid grid-cols-1 gap-8', hasAside && 'lg:grid-cols-[minmax(0,1fr)_19rem]')">
          <article class="flex min-w-0 flex-col gap-6">
            <p v-if="staleWarning" role="status" class="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-4 py-2.5 text-sm">
              <Clock class="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span class="flex-1">{{ t.knowledge.staleWarning(ageDays) }}</span>
              <Button v-if="can('knowledge.write')" size="sm" variant="outline" class="h-7" @click="setQuery({ edit: '1' })"><PencilLine /> Review</Button>
            </p>
            <AiSummaryBox v-if="data.summary" :summary="data.summary" />
            <nav v-if="headings.length > 2" aria-label="On this page" class="rounded-lg border bg-muted/30 px-4 py-3 text-sm xl:hidden">
              <p class="eyebrow mb-1.5">On this page</p>
              <ul class="flex flex-wrap gap-x-4 gap-y-1">
                <li v-for="h in headings" :key="h.id"><a :href="`#${h.id}`" class="text-muted-foreground hover:text-foreground">{{ h.text }}</a></li>
              </ul>
            </nav>
            <MarkdownView v-if="entity.bodyMd?.trim()" :source="entity.bodyMd" />
            <!-- No text, but facts or relations: a compact note, the aside carries the content. -->
            <p
              v-else-if="data.facts.length || relatedGroups.length"
              role="note"
              class="flex flex-wrap items-center gap-3 rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground"
            >
              <span class="flex-1">{{ t.knowledge.noBodyWithFacts }}</span>
              <Button v-if="can('knowledge.write')" size="sm" variant="outline" class="h-7" @click="setQuery({ edit: '1' })"><PencilLine /> Start writing</Button>
            </p>
            <EmptyState v-else :icon="PencilLine" :title="t.knowledge.noBody" :description="t.knowledge.noBodyHint">
              <Button v-if="can('knowledge.write')" @click="setQuery({ edit: '1' })"><PencilLine /> Start writing</Button>
            </EmptyState>

            <section v-if="data.mentions.length" aria-labelledby="mentions">
              <h2 id="mentions" class="section-title mb-3">{{ t.knowledge.mentionedIn }}</h2>
              <ul class="flex flex-col gap-2">
                <li v-for="m in data.mentions" :key="m.documentId">
                  <Card variant="interactive" padding="none" as-child class="gap-1 rounded-lg px-4 py-3">
                    <RouterLink :to="`/k/doc/${encodeURIComponent(m.documentId)}`">
                      <span class="flex items-center gap-2 text-sm font-medium"><FileText class="size-4 text-muted-foreground" aria-hidden="true" /> {{ m.title }}</span>
                      <span class="line-clamp-2 text-sm text-muted-foreground">{{ m.snippet }}</span>
                    </RouterLink>
                  </Card>
                </li>
              </ul>
            </section>
          </article>

          <aside v-if="hasAside" class="flex min-w-0 flex-col gap-4">
            <SectionCard v-if="data.facts.length" :title="t.knowledge.atAGlance">
              <FactsCard :facts="data.facts" />
            </SectionCard>

            <SectionCard v-if="relatedGroups.length" :title="t.knowledge.related">
              <div class="flex flex-col gap-3">
                <div v-for="[label, items] in relatedGroups" :key="label">
                  <button
                    v-if="items.length > RELATED_PREVIEW"
                    type="button"
                    class="mb-1 flex w-full items-center gap-1 text-left text-xs font-medium text-muted-foreground hover:text-foreground"
                    :aria-expanded="expanded.has(label)"
                    @click="toggleGroup(label)"
                  >
                    <component :is="expanded.has(label) ? ChevronDown : ChevronRight" class="size-3.5" aria-hidden="true" />
                    <span class="flex-1">{{ label }}</span>
                    <span class="tabular-nums">{{ items.length }}</span>
                  </button>
                  <p v-else class="mb-1 flex items-center text-xs font-medium text-muted-foreground">
                    <span class="flex-1">{{ label }}</span><span class="tabular-nums">{{ items.length }}</span>
                  </p>
                  <ul class="flex flex-col gap-0.5">
                    <li v-for="r in expanded.has(label) ? items : items.slice(0, RELATED_PREVIEW)" :key="r.entity.uri">
                      <RouterLink :to="entityPath(r.entity.type, r.entity.slug)" class="-mx-1.5 flex items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-muted">
                        <component :is="typeInfo(r.entity.type).icon" class="size-3.5 shrink-0" :style="{ color: typeInfo(r.entity.type).color }" aria-hidden="true" />
                        <span class="truncate">{{ r.entity.name }}</span>
                      </RouterLink>
                    </li>
                  </ul>
                  <button
                    v-if="items.length > RELATED_PREVIEW && !expanded.has(label)"
                    type="button"
                    class="mt-0.5 text-xs font-medium text-primary hover:underline"
                    @click="toggleGroup(label)"
                  >
                    Show all {{ items.length }}
                  </button>
                </div>
              </div>
            </SectionCard>

            <SectionCard v-if="graphUseful" :title="t.knowledge.neighbourhood">
              <template #actions>
                <Button as-child variant="ghost" size="icon-sm" aria-label="Open larger">
                  <RouterLink :to="{ path: '/knowledge/views', query: { view: 'neighborhood', uri: entity.uri } }"><Maximize2 /></RouterLink>
                </Button>
              </template>
              <GraphCanvas
                v-if="graph.data.value"
                :nodes="graph.data.value.nodes"
                :edges="graph.data.value.edges"
                :focus-id="graph.data.value.nodes.find((n) => n.uri === entity!.uri)?.id"
                compact
                height="16rem"
                direction="DOWN"
                :aria-label="`Neighbourhood of ${entity.name}`"
                @select="openNode"
              />
            </SectionCard>

            <nav v-if="headings.length > 2" aria-label="On this page" class="hidden rounded-lg border bg-card px-4 py-3 text-sm xl:block">
              <p class="eyebrow mb-1.5">On this page</p>
              <ul class="flex flex-col gap-1">
                <li v-for="h in headings" :key="h.id" :class="h.level === 3 && 'pl-3'">
                  <a :href="`#${h.id}`" class="text-muted-foreground hover:text-foreground">{{ h.text }}</a>
                </li>
              </ul>
            </nav>
          </aside>
        </div>
      </template>
    </template>
  </div>
</template>
